import React, { useState } from 'react';

import Icon from '../primitives/Icon';
import TextBubble from './TextBubble';
import { Card, CardContent } from '../../components/ui/card';

/**
 * 2_1_classify's confirmation message, cold-start variant: a Data Retrieval
 * list (one bullet per warehouse table, each with its own SQL toggle) above
 * the usual target-column/problem-type/confirm-question text. One combined
 * message — the user's single "yes" reply confirms the plan AND authorizes
 * building this data (see agent_tools.tool_materialize_analysis_dataset).
 *
 * Props: blockData = {
 *   text,
 *   data_retrieval: [{ table, description, sql }],
 *   label_split?: { target_column, labelled, to_predict },
 * }
 */
const ClassifyPlanBlock = ({ blockData }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const data = blockData || {};
  const retrieval = data.data_retrieval || [];
  const split = data.label_split;

  const toggle = (i) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <Card style={{ marginTop: 10 }}>
      <CardContent>
      {retrieval.length > 0 && (
        <div style={{ marginBottom: 16, fontSize: 'var(--font-size-base)' }}>
          <div style={{ fontWeight: 500, color: 'var(--fg-strong)', marginBottom: 6 }}>
            Data Retrieval
          </div>
          <div style={{ color: 'var(--fg-muted)', marginBottom: 8 }}>
            Queries run against the data warehouse:
          </div>
          {split && split.to_predict != null && (
            <div style={{ color: 'var(--fg-muted)', marginBottom: 8 }}>
              Label split{split.target_column ? ` (${split.target_column})` : ''}:{' '}
              {Number(split.labelled).toLocaleString()} known outcomes ·{' '}
              {Number(split.to_predict).toLocaleString()} to predict
            </div>
          )}
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {retrieval.map((t, i) => (
              <li key={i} style={{ color: 'var(--fg)', lineHeight: 1.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18 }}>
                  <span>
                    <span style={{ fontWeight: 500, color: 'var(--foreground)' }}>{t.table}</span>: {t.description}
                  </span>
                  {t.sql && (
                    <button
                      type="button"
                      onClick={() => toggle(i)}
                      title={expanded.has(i) ? 'Hide SQL' : 'Show SQL'}
                      style={{
                        padding: '2px 6px', borderRadius: 5,
                        border: '1px dashed var(--line-soft)', background: 'transparent',
                        color: 'var(--foreground)', cursor: 'pointer', flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        fontSize: 11,
                      }}
                    >
                      SQL
                      <Icon name={expanded.has(i) ? 'chevron-up' : 'chevron-down'} size={10} />
                    </button>
                  )}
                </div>
                {expanded.has(i) && t.sql && (
                  <pre style={{
                    fontSize: 11.5, color: 'var(--muted-foreground)', background: 'var(--muted)',
                    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px',
                    marginTop: 6, marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {t.sql}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <TextBubble blockData={{ text: data.text || '' }} />
      </CardContent>
    </Card>
  );
};

export default ClassifyPlanBlock;
